/*
 * ******************************************************************
 * Copyright (c) 2021-2025 Broadcom. All Rights Reserved.
 * Broadcom Confidential. The term "Broadcom" refers to Broadcom Inc.
 * and/or its subsidiaries.
 * ******************************************************************
 */
import { Directive, Input, OnInit, TemplateRef, ViewContainerRef } from "@angular/core";
import { RbacService } from "@app/services";
import { UiPrivilegeEnum } from "@shared/services/rbac/roles.model";

/**
 * A single privilege is required to perform an action
 * *requiredPrivileges="[UiPrivilegeEnum.CREATE_DEPLOYMENT]"
 *
 * Multiple privileges are required to perform an action
 * *requiredPrivileges="[UiPrivilegeEnum.CREATE_DEPLOYMENT, UiPrivilegeEnum.DELETE_DEPLOYMENT]; requireAll=true"
 *
 * Requires a role
 * *requiredPrivileges="requiredRole=..."
 */
// @formatter:off
@Directive({
    standalone: false,
    selector: "[requiredPrivileges]"
})
// @formatter:on
export class RequiredPrivilegesDirective implements OnInit {
    private _requiredPrivileges: UiPrivilegeEnum[];
    private _requireAll: boolean = false;
    private _requiredRole: string;
    private _condition: boolean = true;

    constructor(private templateRef: TemplateRef<any>,
                private viewContainer: ViewContainerRef,
                private rbacService: RbacService) {
    }

    @Input()
    set requiredPrivileges(privs: UiPrivilegeEnum[]) {
        this._requiredPrivileges = privs;
    }

    @Input()
    set requiredPrivilegesRequireAll(requireAll: boolean) {
        this._requireAll = requireAll;
    }

    @Input()
    set requiredPrivilegesRequiredRole(role: string) {
        this._requiredRole = role;
    }

    @Input()
    set requiredPrivilegesCondition(cond: boolean) {
        this._condition = cond;
    }

    ngOnInit(): void {
        if (this._requiredPrivileges != null && this._requiredRole != null) {
            console.log("Wrong directive arguments");
            return;
        }

        if (!this._condition) {
            this.hideControl();
            return;
        }

        if (this._requiredRole != null) {
            this.showControl(this.rbacService.hasRole(this._requiredRole));
            return;
        }

        if (this._requiredPrivileges == null || this._requiredPrivileges.length == 0) {
            this.showControl(true);
            return;
        }

        const noPrivileges: boolean = this._requiredPrivileges.includes(UiPrivilegeEnum.NO_PRIVILEGES);
        if (noPrivileges && this._requireAll) {
            console.log("Conflicting directive arguments.");
            return;
        }

        if (this._requireAll === true) {
            this.showControl(this.rbacService.hasAllPrivileges(this._requiredPrivileges));
        } else {
            if (noPrivileges && this.rbacService.hasNoPrivileges()) {
                this.showControl(true);
            } else {
                this.showControl(this.rbacService.hasOneOfPrivileges(this._requiredPrivileges));
            }
        }
    }

    private showControl(visible: boolean) {
        if (visible) {
            this.viewContainer.createEmbeddedView(this.templateRef);
        } else {
            this.viewContainer.clear();
        }
    }

    private hideControl() {
        this.viewContainer.clear();
    }
}
